import {
  DEFAULT_PROPERTIES_CONFIG, DEFAULT_RENDER_CONFIG,
  DefaultPropertyConfig,
  InboxDbConfig,
  OutboxDbConfig, OutboxRenderPreferences,
  Profile
} from "@app/core-app/app/data/profile";

export class ProfileService {

  static FREE_SAVING_LIMIT = 100
  static FREE_SENDING_LIMIT = 50
  static PREMIUM_SENDING_LIMIT = 5000
  static PREMIUM_SAVING_LIMIT = 10000


  static getFromEmailAddress({profile, databaseId}: {
    profile: Profile,
    databaseId?: string | undefined | null
  }): string {

    if (databaseId) {
      const config = profile.dbConfigurations?.find(db => db.databaseId == databaseId)
      if (config?.outbox?.fromEmailAddress) {
        return config.outbox.fromEmailAddress
      }
    }

    if (profile.emailVerification?.email) {
      return profile.emailVerification.email
    } else {
      return "pages@notionsender.com"
    }
  }

  static formatFromAddress({profile, databaseId, address}: {
    profile: Profile,
    databaseId?: string | undefined | null,
    address: string
  }): string {

    if (address === "pages@notionsender.com") {
      return `NotionSender <${address}>`
    }

    if (databaseId) {
      const config = profile.dbConfigurations?.find(db => db.databaseId == databaseId)
      if (config?.outbox?.fromSenderName) {
        return `${config.outbox.fromSenderName} <${address}>`
      }
    }

    return address

  }

  static getReplyToEmailAddress({profile, databaseId}: {
    profile: Profile,
    databaseId?: string | undefined | null
  }): string | null {

    if (databaseId) {
      const config = profile.dbConfigurations?.find(db => db.databaseId == databaseId)
      if (config?.outbox?.replyToEmailAddress) {
        return config.outbox.replyToEmailAddress
      }
    }

    return null
  }

  static findOutboxConfig({profile, databaseId}: {
    profile: Profile,
    databaseId: string
  }): OutboxDbConfig | null | undefined {
    const dbConfig = profile.dbConfigurations
      ?.find(db => db.databaseId == databaseId) || null

    return dbConfig?.outbox

  }

  static findOutboxRenderPreferences({profile, databaseId}: {
    profile: Profile,
    databaseId: string
  }): OutboxRenderPreferences  {
    const dbConfig = profile.dbConfigurations
      ?.find(db => db.databaseId == databaseId) || null

    const renderConfig = dbConfig?.outbox?.renderConfiguration || {}

    return {
      ...DEFAULT_RENDER_CONFIG,
      ...renderConfig
    }

  }

  static findInboxConfig({profile, databaseId}: {
    profile: Profile,
    databaseId: string
  }): InboxDbConfig | null | undefined {
    const dbConfig = profile.dbConfigurations?.find(db => db.databaseId == databaseId) || null
    return dbConfig?.inbox
  }

  static getSendingLimit(profile: Profile): number {
    if (profile.premium) {
      return profile.customSendingLimit || ProfileService.PREMIUM_SENDING_LIMIT
    }
    return ProfileService.FREE_SENDING_LIMIT
  }

  static getSavingLimit(profile: Profile): number {
    if (profile.premium) {
      return ProfileService.PREMIUM_SAVING_LIMIT
    }
    return ProfileService.FREE_SAVING_LIMIT
  }


  static addDefaultPropertyToInbox({profile, databaseId, property}: {
    profile: Profile,
    databaseId: string,
    property: DefaultPropertyConfig
  }):
    {
      inbox: InboxDbConfig | undefined,
      outbox: OutboxDbConfig | undefined
    } {
    const dbConfig = profile.dbConfigurations?.find(db => db.databaseId == databaseId) || null

    if (!dbConfig) {
      throw new Error("Database not found")
    }
    const inboxConfig = dbConfig.inbox

    if(!inboxConfig){
      throw new Error("Inbox not configured")
    }

    const currentDefaultPropConfig = dbConfig.inbox?.defaultPropertiesConfig || DEFAULT_PROPERTIES_CONFIG
    const currentProperties = currentDefaultPropConfig?.properties ?? []

    const updatedProperties: DefaultPropertyConfig[] = [...currentProperties, property]

    return {
      outbox: undefined,
      inbox: {
        ...inboxConfig,
        defaultPropertiesConfig: {
          ...(currentDefaultPropConfig),
          properties: updatedProperties,

        }
      }
    }
  }

  static removeDefaultPropertyFromInbox({profile, databaseId, propertyName}: {
    profile: Profile,
    databaseId: string,
    propertyName: string
  }): {
    inbox: InboxDbConfig | undefined,
    outbox: OutboxDbConfig | undefined
  }{

    const dbConfig = profile.dbConfigurations?.find(db => db.databaseId == databaseId) || null
    if (!dbConfig) {
      throw new Error("Database not found")
    }
    const inboxConfig = dbConfig.inbox
    if(!inboxConfig){
      throw new Error("Inbox not configured")
    }
    const currentDefaultPropConfig = dbConfig.inbox?.defaultPropertiesConfig || DEFAULT_PROPERTIES_CONFIG
    const currentProperties = currentDefaultPropConfig?.properties ?? []
    const updatedProperties: DefaultPropertyConfig[] = currentProperties.filter(prop => prop.name !== propertyName)
    return {
      outbox: undefined,
      inbox: {
        ...inboxConfig,
        defaultPropertiesConfig: {
          ...(currentDefaultPropConfig),
          properties: updatedProperties,

        }
      }
    }
  }

}